import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { initialCalendarMonth } from "../../DeclarationCourse/sessionCalendarHelpers";
import { SessionRow, sessionEnd, sessionStart } from "../../DeclarationCourse/SessionList";
import { MAX_COURSES_PER_MONTH } from "../../../Utils/monthlyQuota";
import { DV2 } from "../theme";
import { clock, dayLabel, monthName, studentFirstName, studentFullName } from "../format";
import { vocab } from "../vocabulary";
import { useDeclarationV2 } from "../DeclarationV2Context";
import WizardShell from "../layout/WizardShell";
import PlanningAside from "../layout/PlanningAside";
import StudentMonthPanel, { studentMonthFigures } from "../layout/StudentMonthPanel";
import { Badge, Card, Notice, SectionTitle, TextLink } from "../layout/Ui";
import MonthCalendar from "../components/MonthCalendar";
import { DurationStepper, TimeSelect } from "../components/TimeFields";
import useMonthSessions from "../components/useMonthSessions";
import PastCourseDialog from "../components/PastCourseDialog";
import { FreeSlot, dateConflict, describeExistingConflict, describeShift, earliestStartOn, endsNextDay, firstSelectableDay, nextFreeStart, rowIssues, timeToIso, timeValue } from "../components/planningRules";
import { fetchExternalOverlap } from "../../DeclarationCourse/sessionConflicts";

const nextQuarterHour = (): string => {
    const now = moment().startOf("minute");
    const remainder = now.minute() % 15;
    if (remainder !== 0) now.add(15 - remainder, "minute");
    return now.format("HH:mm");
};

/**
 * Étape 2, un seul cours (maquettes 1i et 2i) : calendrier à date unique,
 * heure de début et durée, vérification du créneau en direct, puis Suivant.
 * Règles de l'ancien SecondStep : date à partir de la validation du compte,
 * dans le mois en cours, pas moins de 2 h 30 après la validation, quota du
 * mois, confirmation pour un cours déjà passé.
 */
const PlanningSingleStep = () => {
    const { teachr, state, update, goTo } = useDeclarationV2();
    const student = state.student!;
    const child = student.child;
    const minDate = firstSelectableDay(student.activationDate);
    const firstMonth = minDate.slice(0, 7);
    const lastMonth = moment().add(1, "month").format("YYYY-MM");
    const { date, startTime, duration } = state.single;
    const time = timeValue(startTime);

    const [month, setMonth] = useState(() => (date ? moment(date).startOf("month") : initialCalendarMonth([], minDate)));
    const [error, setError] = useState<string | null>(null);
    const [pastOpen, setPastOpen] = useState(false);
    const [checking, setChecking] = useState(false);
    /** Début décalé après un cours existant au choix de la date, pour l'expliquer. */
    const [shift, setShift] = useState<FreeSlot | null>(null);

    const { sessions: existing, failed } = useMonthSessions(teachr?.id, month);
    const { sessions: clientSessions, loading: clientLoading } = useMonthSessions(teachr?.id, month, child?.clientId);

    /** Le jour de la validation du compte, l'heure est décalée au plus tôt possible (13h47 arrondi à 13h50). */
    const withEarliest = (single: typeof state.single): typeof state.single => {
        const earliest = earliestStartOn(single.date, student.activationDate);
        const current = timeValue(single.startTime);
        if (!earliest || !single.date || !current || current >= earliest) return single;
        return { ...single, startTime: timeToIso(earliest, single.date) };
    };
    const earliestToday = earliestStartOn(date, student.activationDate);
    const nextDayEnd = endsNextDay(time, duration);

    // Heure proposée par défaut : le prochain quart d'heure.
    useEffect(() => {
        if (!startTime) update({ single: withEarliest({ ...state.single, startTime: timeToIso(nextQuarterHour(), date) }) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const row: SessionRow = useMemo(() => ({ key: "single", date, startTime, duration }), [date, startTime, duration]);
    const start = sessionStart(row);
    const end = sessionEnd(row);
    const conflict = date && time ? dateConflict(date, time, duration, existing) : null;
    const figures = studentMonthFigures(child, teachr);
    const isCurrentMonth = !!date && date.slice(0, 7) === moment().format("YYYY-MM");
    const quotaReached = isCurrentMonth && !!figures.quota?.reached;

    const setSingle = (patch: Partial<typeof state.single>) => {
        setError(null);
        setShift(null);
        update({ single: withEarliest({ ...state.single, ...patch }) });
    };

    /** Choix d'une date : si le créneau chevauche un cours existant, on garde la date et le début passe juste après ce cours. */
    const pickDate = (d: string) => {
        const nextDate = d === date ? null : d;
        const free = nextDate && time ? nextFreeStart(nextDate, time, duration, existing) : null;
        const nextTime = free ? free.time : time;
        setSingle({ date: nextDate, startTime: nextTime && nextDate ? timeToIso(nextTime, nextDate) : startTime });
        setShift(free);
    };

    const proceed = () => {
        setPastOpen(false);
        update({ step: "pricing" });
        goTo("pricing");
    };

    const next = async () => {
        if (!date) return setError(`Choisissez le jour ${vocab.ofTheCourse()} dans le calendrier.`);
        if (!time) return setError("Choisissez l'heure de début.");
        if (quotaReached) return setError(`Quota de ${MAX_COURSES_PER_MONTH} ${vocab.courses()} atteint pour ${studentFirstName(child)} en ${monthName(moment())}. Choisissez une date du mois suivant.`);
        const issues = rowIssues([row], { activationDate: student.activationDate, existing, referenceMonth: date.slice(0, 7) });
        if (issues.length > 0) return setError(issues[0].message);
        if (failed && teachr?.id) {
            // les séances du mois n'ont pas pu être chargées : contrôle ciblé avant l'envoi
            setChecking(true);
            const overlap = await fetchExternalOverlap(teachr.id, [row]).finally(() => setChecking(false));
            if (overlap) return setError(`Ce créneau chevauche ${vocab.theCourse()} déjà déclaré avec ${overlap.studentName} (${overlap.existingSlot}).`);
        }
        if (end && end.isBefore(moment())) {
            setPastOpen(true);
            return undefined;
        }
        return proceed();
    };

    return (
        <>
            <PastCourseDialog
                open={pastOpen}
                description={start && end ? <>le <b>{dayLabel(start).toLowerCase()}</b> de <b>{clock(start)}</b> à <b>{clock(end)}</b></> : null}
                onConfirm={proceed}
                onClose={() => setPastOpen(false)}
            />
            <WizardShell
                title={`Quand a lieu ${vocab.theCourse()} ?`}
                subtitle="Étape 2 sur 4"
                onBack={() => goTo("student")}
                asideMobile={<StudentMonthPanel child={child} teachr={teachr} compact />}
                aside={<PlanningAside current={2} month={month} clientSessions={clientSessions} clientSessionsLoading={clientLoading} />}
                footer={{
                    onPrevious: () => goTo("student"),
                    onNext: next,
                    nextLoading: checking,
                    extra: error ? <Notice tone="danger" id="dv2-single-error">{error}</Notice> : undefined,
                }}
            >
                <div className="flex items-center gap-2 flex-wrap" style={{ fontSize: 14 }}>
                    <Badge>{vocab.singleCourseTitle()}</Badge>
                    <span style={{ color: DV2.muted }}>avec {studentFullName(child)}</span>
                    <TextLink label="Changer" onClick={() => goTo("student")} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-5 items-start">
                    <Card style={{ display: "flex", flexDirection: "column", gap: 16, padding: 22 }}>
                        <div style={{ fontSize: 14, color: DV2.muted, lineHeight: 1.5 }}>
                            Choisissez la date {vocab.ofTheCourse()}, dans le mois en cours et à partir de la validation du compte de {vocab.yourClient()}.
                        </div>
                        <MonthCalendar
                            month={month}
                            onMonthChange={setMonth}
                            firstMonth={firstMonth}
                            lastMonth={lastMonth}
                            selected={date ? [date] : []}
                            minDate={minDate}
                            onToggleDate={pickDate}
                            conflicts={conflict && date ? new Set([date]) : undefined}
                        />
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", fontSize: 13, color: DV2.muted }}>
                            <span>{date ? <>Date choisie, <b style={{ color: DV2.text }}>{dayLabel(date, true).toLowerCase()}</b></> : "Aucune date choisie"}</span>
                            <span>Aujourd'hui, le {moment().date()}</span>
                        </div>
                        {quotaReached && (
                            <Notice tone="danger">
                                Quota de {MAX_COURSES_PER_MONTH} {vocab.courses()} atteint pour {studentFirstName(child)} en {monthName(moment())}. Choisissez une date du mois suivant.
                            </Notice>
                        )}
                        {earliestToday && (
                            <Notice tone="info">
                                Le compte de {studentFirstName(child)} a été validé ce jour-là à {clock(student.activationDate!)} : {vocab.theCourse()} ne peut pas commencer avant {clock(timeToIso(earliestToday))}.
                            </Notice>
                        )}
                    </Card>

                    <div className="flex flex-col gap-3">
                        <Card style={{ display: "flex", flexDirection: "column", gap: 16, padding: 22 }}>
                            <SectionTitle>Horaire {vocab.ofTheCourse()}</SectionTitle>
                            <TimeSelect id="dv2-single-time" value={time} onChange={(value) => setSingle({ startTime: timeToIso(value, date) })} error={!!error && !time} />
                            <DurationStepper id="dv2-single-duration" value={duration} onChange={(value) => setSingle({ duration: value })} />
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, borderTop: `1px solid ${DV2.borderSoft}`, paddingTop: 12 }}>
                                <span style={{ color: DV2.muted }}>Fin de séance</span>
                                <b>{end ? clock(end) : time ? clock(moment(timeToIso(time)).add(duration, "hours")) : ""}</b>
                            </div>
                        </Card>
                        {nextDayEnd && (
                            <Notice tone="danger">
                                Avec ce début et cette durée, {vocab.theCourse()} se terminerait le lendemain à {nextDayEnd.replace(":", "h")}. Réduisez la durée ou avancez l'heure de début.
                            </Notice>
                        )}
                        {start && end && !nextDayEnd && (
                            conflict ? (
                                <Notice tone="warning">
                                    <b>{dayLabel(start)}, {clock(start)} à {clock(end)}.</b> {describeExistingConflict(conflict)} Décalez le début à {clock(conflict.endDate)} ou plus tard.
                                </Notice>
                            ) : shift ? (
                                <Notice tone="info">
                                    <b>{dayLabel(start)}, {clock(start)} à {clock(end)}.</b> {describeShift(shift.time, shift.after)} Vous pouvez choisir une autre heure.
                                </Notice>
                            ) : (
                                <Card tint style={{ padding: "16px 18px" }}>
                                    <div style={{ fontWeight: 800, fontSize: 14 }}>{dayLabel(start)}, {clock(start)} à {clock(end)}</div>
                                    <div style={{ fontSize: 13, color: DV2.textSoft }}>Aucun autre {vocab.course()} prévu sur ce créneau</div>
                                </Card>
                            )
                        )}
                    </div>
                </div>
            </WizardShell>
        </>
    );
};

export default PlanningSingleStep;
